import { defineStore } from "pinia";
import { FetchRequest } from "../helpers/fetch-request";
import { useStorage } from 'nvd-use-storage'
import { COPIED_OBJECT_KEY, data_types, TABS_KEY } from "@/data/constants";
import { undoRedoActions, undoRedoGetters } from "@/stores/undoRedo";
import { tabHelper } from "@/helpers/tabHelper";
import { _deepClone } from "@/helpers/misc";
import { defaultHistory, newComment } from "@/data/tab-template";
import { ApmCanvas } from "@/modules/canvas/ApmCanvas";
import { SchemaToDbml } from "@/components/code-editor/schemaToDbml";
import router from "@/router/routes";
import { env } from "@/env";
import { useMenuStore } from "@/stores/menu.store";
import dayjs from "dayjs";
import { useAuthStore } from "@/stores/auth.store";
import { UpdateTableCommand } from "@/commands/UpdateTableCommand";
import { useSchemaStore } from "@/stores/schema.store";
import { DeleteTableCommand } from "@/commands/DeleteTableCommand";
import { Storage } from "nvd-js-helpers/storage-helper";
import { DeleteMultipleItemsCommand } from "../commands/DeleteMultipleItemsCommand";
import i18n from "@/locales/translations";
import { TabSettings } from "../classes/TabSettings";
import { useConfirm } from "nvd-u/composables/Confirm";
import { ensureLoggedInForSchemaEdit } from "@/helpers/edit-access";
import { hasStoredItemPositions } from "@/helpers/item-position.js";

let tabs = Storage.getObject(TABS_KEY, false)
if (tabs && tabs.length) {
    tabs = tabs.filter(tab => {
        tab.updated = false
        return !!tab.schema.slug
    })
}

function hasShareableView() {
    if (typeof window === 'undefined') return false
    const params = new URLSearchParams(window.location.search)
    return params.get('shareable') === 'true'
}

export const useTabsStore = defineStore('tabs', {
    state: () => ({
        tabs: tabs?.length ? tabs : [],
        selectedTab: null,
        dataTypeReq: new FetchRequest('data_types', 'GET'),
        aiDbTypesReq: new FetchRequest('supported_databases', 'get'),
        createCommentReq: new FetchRequest('', 'POST'),
        getCommentsReq: new FetchRequest('', 'GET'),
        restoreReq: new FetchRequest('', 'GET'),
        copiedObject: useStorage(COPIED_OBJECT_KEY),
        showNewTabConfirmationDialogue: false
    }),
    getters: {
        ...undoRedoGetters,
        dataTypesList: state => state.dataTypeReq.data.data_types ? state.dataTypeReq?.data?.data_types[state.selectedTab?.schema?.db] : [],
        dbTypes: state => Object.keys(state.dataTypeReq.data?.data_types || {}),
        aiDbTypes: state => {
            if (state.aiDbTypesReq.loaded && state.aiDbTypesReq.data) return state.aiDbTypesReq.data
            return data_types
        },
        isShareableView: () => hasShareableView(),
        isSchemaReadonly: state => !!state.selectedTab?.schema?.is_read_only || hasShareableView(),
        isTemplateReadonly: state => state.selectedTab?.schema?.template && !state.selectedTab?.schema?.is_owner,
    },
    actions: {
        resetAccountState() {
            this.$reset()
            this.tabs = []
            this.selectedTab = null
            this.copiedObject = null
            localStorage.removeItem(COPIED_OBJECT_KEY)
        },
        // mutations
        async selectTab(tab, fitToScreen = false) {
            const auth = useAuthStore()
            const sessionRevision = auth.sessionRevision
            const menu = useMenuStore()
            const schema = useSchemaStore()
            // get the most recent data
            if (tab.hasOwnProperty('updated') && !tab.updated) {
                tab.loading = true
                let req = schema.singleSchemaReq
                req.url = `schemas/${tab.schema.slug}`
                await req.send().then(data => {
                    if (auth.sessionRevision !== sessionRevision) return
                    tab.updated = true
                    tab.schema = data
                    tab.loading = false
                    fitToScreen = true

                    menu.determineMetaData(tab)
                }).catch(error => {
                    if (auth.sessionRevision !== sessionRevision) return
                    if (typeof error === 'string' && error.toLowerCase().includes('schema does not exist') || error.toLowerCase().includes('does not belong')) {
                        this.tabs.splice(this.tabs.findIndex(t => t.id === tab.id), 1)
                        Storage.setObject(TABS_KEY, this.tabs)
                        window.location.href = '/dashboard'
                    }
                })
            }
            if (auth.sessionRevision !== sessionRevision) return
            tab.id = tabHelper.tabId(tab)
            tab.dbml = tab.dbml || ''
            tab.history = tab.history || _deepClone(defaultHistory)
            tab.comments = tab.comments || []
            tab.canvas = tab.canvas || new ApmCanvas(tab)
            tab.newComment = tab.newComment || { ...newComment }
            tab.settings = tab.settings || new TabSettings()

            await new SchemaToDbml(tab).convert()
            if (auth.sessionRevision !== sessionRevision) return

            this.selectedTab = tab

            let id = tab.schema.slug || tab.schema.id || 'new'
            let url = `/designer/schema/${id}`
            const params = new URLSearchParams(window.location.search)
            if (menu.embedded) {
                url = `/designer/schema/${id}?embed=true`
            } else if (params.get('shareable') === 'true') {
                url = `/designer/schema/${id}?shareable=true`
            }
            router.push(url)
            document.title = tab.schema.title + " | " + env.appName

            setTimeout(() => {
                if (auth.sessionRevision !== sessionRevision || this.selectedTab !== tab) return
                // check if it needs auto sorting
                const items = tab.schema.schema_data.tables.concat(tab.schema.schema_data.notes)
                if (items.length && !hasStoredItemPositions(items))
                    schema.sortItems()
            }, 500)


            if (fitToScreen) {
                setTimeout(() => {
                    if (auth.sessionRevision === sessionRevision && this.selectedTab === tab) menu.handleMenuItemClick({ id: 'fitToScreen' })
                }, 100)
            }
        },
        setCopiedObject(obj) {
            this.copiedObject = obj
            Storage.setObject(COPIED_OBJECT_KEY, obj)
        },
        setShowNewTabConfirmationDialogue(payload) {
            this.showNewTabConfirmationDialogue = payload
        },
        // actions
        ...undoRedoActions,
        selectFirstTab() {
            this.selectTab(this.tabs[0])
        },
        saveTabsInStorage() {
            let tabs = this.tabs.map(tab => ({
                schema: _deepClone(tab.schema),
                settings: tab.settings
            }))
            Storage.setObject(TABS_KEY, tabs, false)
        },
        loadComments() {
            if (!this.selectedTab?.schema?.slug) return
            if (this.isSchemaReadonly || this.isTemplateReadonly) return
            if(!this.selectedTab.schema.id || this.selectedTab.schema.template) return
            const selectedTab = this.selectedTab
            const request = this.getCommentsReq
            request.url = `schemas/${selectedTab.schema.slug}/schema_comments`
            return request.send().then(comments => {
                if (this.getCommentsReq === request && this.selectedTab === selectedTab) selectedTab.comments = comments
            })
        },
        createComment() {
            const auth = useAuthStore()
            this.createCommentReq.url = `schemas/${this.selectedTab.schema.id}/schema_comments`
            let dataToSend = {
                ...this.selectedTab.newComment,
                commenter: auth.user?.tmpid,
            }

            let comment = {
                ...dataToSend,
                user_id: dataToSend.commenter,
                commenter: auth.user?.name,
                time: dayjs().format('DD MMM YYYY HH:mm'),
                avatar: auth.user.avatar
            }
            this.selectedTab.comments.push(comment)
            this.selectedTab.newComment = { ...newComment }

            this.createCommentReq.send({ body: JSON.stringify(dataToSend) }).then(res => {
                let _comment = res.content ? res : res.pop()
                for (const key in _comment) {
                    comment[key] = _comment[key]
                }
            }).catch(res => {
                this.selectedTab.comments.shift()
            })
        },
        saveTable(data) {
            if (!ensureLoggedInForSchemaEdit()) return
            let command = new UpdateTableCommand(this.selectedTab, {
                table: this.selectedTab.canvas.editedTable,
                data
            })
            if (this.selectedTab.canvas.editedTable.$newTable) {
                delete this.selectedTab.canvas.editedTable.$newTable
            }
            command.execute()

            this.selectedTab.canvas.setEditedTable(null)
        },
        deleteTable(table) {
            if (!ensureLoggedInForSchemaEdit()) return
            (new DeleteTableCommand(this.selectedTab, { table })).execute()
            this.selectedTab.canvas.setEditedTable(null)
            this.selectedTab.canvas.setSelectedObject(null)
            this.selectedTab.canvas.setHoveredObject(null)
        },
        closeTab() {
            this.saveTabsInStorage()
        },
        async handleWindowClosed() {
            document.body.onbeforeunload = e => {
                let unsavedTabs = this.tabs.filter(tab => tab.unsaved)
                if (unsavedTabs.length) {
                    e.preventDefault()
                    return 'There is unsaved data. Are you sure you want to close this window?'
                }
            }
        },
        resetCopiedObject() {
            this.copiedObject = null
            Storage.setObject(COPIED_OBJECT_KEY, null)
        },
        findTabBySchemaId(schemaId) {
            return this.tabs.find(tab => tab.schema?.id == schemaId || tab.id == schemaId)
        },
        async deleteSelectedItems(items = null, options = {}) {
            if (!ensureLoggedInForSchemaEdit()) return
            const selectedItems = items || this.selectedTab.canvas.selectedItems.items
            const requireConfirm = options?.requireConfirm !== false
            if (!selectedItems?.length) return
            if (requireConfirm && selectedItems.length > 1) {
                const confirm = await useConfirm(
                    this.getDeleteMultipleTitle(selectedItems),
                    this.getDeleteMultipleConfirmMessage(selectedItems)
                )
                if (!confirm) return
            }
            const command = new DeleteMultipleItemsCommand(this.selectedTab, { items: selectedItems })
            command._description = this.getDeleteMultipleTitle(selectedItems)
            command.execute()

            this.selectedTab.canvas.selectedItems.items = []
        },
        clearSelectedItems() {
            this.selectedTab.canvas.selectedItems.items = []
            this.selectedTab.canvas.selectedObject = null
            this.selectedTab.canvas.setSelectedLogicalTable(null)
        },
        getDeleteMultipleTitle(items = null) {
            let title = ''
            const selectedItems = items || this.selectedTab?.canvas?.selectedItems?.items || []
            if (!selectedItems.length) return title
            let isTables = selectedItems.findIndex(obj => !!obj.fields)
            let isNotes = selectedItems.findIndex(obj => !!!obj.fields)
            if (isTables !== -1 && isNotes !== -1)
                title = i18n.global.t('designer.delete_n_items', { count: selectedItems.length })
            else if (isTables !== -1)
                title = i18n.global.t('designer.delete_n_tables', { count: selectedItems.length })
            else
                title = i18n.global.t('designer.delete_n_notes', { count: selectedItems.length })
            return title
        },
        getDeleteMultipleConfirmMessage(items = null) {
            const selectedItems = items || this.selectedTab?.canvas?.selectedItems?.items || []
            if (!selectedItems.length) return ''
            const hasTables = selectedItems.some(obj => !!obj.fields)
            const hasNotes = selectedItems.some(obj => !!!obj.fields)
            let label = 'items'
            if (hasTables && !hasNotes) label = 'tables'
            if (hasNotes && !hasTables) label = 'notes'
            return `Are you sure you want to delete these ${selectedItems.length} ${label}?`
        },
        highlightTableMarkup(tableName) {
            let editor = this.selectedTab.editor
            if (!editor) return

            editor.findAll(tableName, {
                wrap: true,
                caseSensitive: false,
                wholeWord: true,
                regExp: false
            });

            let range = editor.selection.getAllRanges()[0];
            if (range) {
                // Scroll to the first occurrence
                editor.scrollToLine(range.start.row, true, true, function () {
                });
            }
        },
        selectAll() {
            this.selectedTab.canvas.selectedItems.selectMany(this.selectedTab.schema.schema_data.tables.concat(this.selectedTab.schema.schema_data.notes))
        }
    }
})
